import React, { useState, useCallback, useRef, useMemo } from 'react'
import classnames from 'classnames/bind'
import {
  Text,
  Icon,
  Button,
  Chip,
  ChipColor,
  CardContainer,
  LabelValue,
  Divider,
  DropdownField,
  coreConstants,
} from '@adc/ui-morpheus'
import type { PartyBuyingPreferenceInput } from '@adc/sdk/types'
import {
  isIntentValue,
  INTENT_ANY,
  INTENT_NO_MIN,
  INTENT_NO_MAX,
} from '@adc/blueprint'
import type { TypedBuyingPreference } from '../../providers/userInsights'
import type {
  BuyerPreferenceParameters,
  BuyerPreferenceIntents,
} from '../../types/buyerPreference'
import {
  SALE_TYPE_TO_PRODUCT_TYPES,
  PROPERTY_TYPE_TO_FILTER_VALUE,
  PRODUCT_TYPES_TO_SALE_TYPE,
  FILTER_VALUE_TO_PROPERTY_TYPE,
} from '../../types/buyerPreference'
import LocationSearchInput from './locationSearchInput'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface UserBuyingCriteriaProps {
  preference: TypedBuyingPreference | null
  onSave: (input: PartyBuyingPreferenceInput) => void
  isSaving?: boolean
  collapsible?: boolean
  collapsed?: boolean
  onToggle?: () => void
  featureFlags?: Record<string, boolean>
}

// --- Option lists (values are intent-level identifiers) ---

const saleTypeOptions = [
  { text: 'Foreclosure', value: 'FORECLOSURE' },
  { text: 'Bank-Owned', value: 'BANK_OWNED' },
  { text: 'Private Seller', value: 'PRIVATE_SELLER' },
  { text: 'All', value: 'ALL' },
  { text: 'Not Sure', value: 'NOT_SURE' },
]

const occupancyOptions = [
  { text: 'Vacant', value: 'VACANT' },
  { text: 'Occupied', value: 'OCCUPIED' },
  { text: 'Both', value: 'ALL' },
  { text: 'Not Sure', value: 'NOT_SURE' },
]

const propertyTypeOptions = [
  { text: 'Single-Family', value: 'SINGLE_FAMILY' },
  { text: 'Condo/Single-Unit', value: 'CONDOS_AND_TOWNHOUSES' },
  { text: 'Multifamily', value: 'MULTI_FAMILY' },
  { text: 'Land', value: 'LAND' },
  { text: 'Not Sure', value: 'NOT_SURE' },
]

const bedroomOptions = [
  { value: INTENT_ANY, text: 'Any' },
  { value: '1', text: '1' },
  { value: '2', text: '2' },
  { value: '3', text: '3' },
  { value: '4', text: '4' },
  { value: '5', text: '5+' },
]

const bathroomOptions = [
  { value: INTENT_ANY, text: 'Any' },
  { value: '1', text: '1' },
  { value: '1.5', text: '1.5' },
  { value: '2', text: '2' },
  { value: '2.5', text: '2.5' },
  { value: '3', text: '3' },
  { value: '4', text: '4' },
]

const priceAmounts = [
  '$50,000',
  '$100,000',
  '$150,000',
  '$180,000',
  '$200,000',
  '$250,000',
  '$300,000',
  '$350,000',
  '$400,000',
  '$420,000',
  '$500,000',
  '$600,000',
  '$750,000',
  '$1,000,000',
].map(s => ({ text: s, value: s }))

const priceMinOptions = [
  { text: 'No Min', value: INTENT_NO_MIN },
  ...priceAmounts,
]

const priceMaxOptions = [
  { text: 'No Max', value: INTENT_NO_MAX },
  ...priceAmounts,
]

const yearOptions = [
  { text: 'Any', value: INTENT_ANY },
  ...['1950', '1960', '1970', '1980', '1990', '2000', '2010', '2020'].map(
    s => ({ text: s, value: s })
  ),
]

// --- Display label lookups ---

const getOptionLabel = (
  value: string,
  options: { text: string; value: string }[]
): string => options.find(o => o.value === value)?.text ?? value

// --- Helpers ---

const formatPrice = (amount: string | null | undefined): string => {
  if (!amount) return '—'
  const num = Number(amount)
  if (isNaN(num)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(num)
}

const parsePriceValue = (val: string): string | null => {
  if (isIntentValue(val)) return val
  const num = val.replace(/[^0-9]/g, '')
  return num || null
}

// Edit-mode dropdown display: intent value shows as-is (matches the option
// value), concrete value is formatted, untouched stays empty so the dropdown
// shows its placeholder (nothing selected until the user picks)
const priceDropdownValue = (stored: string | null | undefined): string => {
  if (!stored) return ''
  return isIntentValue(stored) ? stored : formatPrice(stored)
}

// Dropdown fields whose explicit no-constraint selection is an intent value
// (MERCH-126). The edit state holds the intent string natively; buildSaveInput
// routes it to intents and keeps it out of parameters.
const DROPDOWN_INTENT_KEYS = [
  'price_min',
  'price_max',
  'beds_min',
  'baths_min',
  'yr_bt_min',
] as const
type DropdownIntentKey = (typeof DROPDOWN_INTENT_KEYS)[number]

// Non-filterable values that go in intents but NOT in parameters
const NON_FILTERABLE = new Set(['NOT_SURE', 'ALL'])

/**
 * Build the save input from edit state.
 * Real values → parameters (as filter value), intents = null
 * Intent-only values (ALL/NOT_SURE) → intents (as stated), parameters = null
 * Dropdown fields (MERCH-126): intent value (ANY/NO_MIN/NO_MAX) → intents only;
 * concrete value → parameters + mirrored into intents. parameters never
 * contains an intent value. Exported for tests.
 */
export function buildSaveInput(
  editParams: BuyerPreferenceParameters,
  editIntents: BuyerPreferenceIntents
): PartyBuyingPreferenceInput {
  const parameters: BuyerPreferenceParameters = {
    price_min: null,
    price_max: null,
    beds_min: null,
    baths_min: null,
    yr_bt_min: null,
    locations: editParams.locations || null,
    preference_notes: editParams.preference_notes || null,
    occ_status: null,
    usecode_product_type: null,
    usecode_property_type: null,
  }

  const intents: BuyerPreferenceIntents = {
    occ_status: null,
    usecode_product_type: null,
    usecode_property_type: null,
    price_min: null,
    price_max: null,
    beds_min: null,
    baths_min: null,
    yr_bt_min: null,
  }

  // Dropdown fields: route intent values to intents, mirror concrete values
  DROPDOWN_INTENT_KEYS.forEach((key: DropdownIntentKey) => {
    const value = editParams[key] || null
    if (value && isIntentValue(value)) {
      intents[key] = value
    } else {
      parameters[key] = value
      intents[key] = value
    }
  })

  // Occupancy: filterable value → parameters, non-filterable → intents
  if (editIntents.occ_status) {
    if (NON_FILTERABLE.has(editIntents.occ_status)) {
      intents.occ_status = editIntents.occ_status
    } else {
      parameters.occ_status = editIntents.occ_status
    }
  }

  // Sale type: filterable value → parameters (mapped to seek codes), non-filterable → intents
  if (editIntents.usecode_product_type) {
    if (NON_FILTERABLE.has(editIntents.usecode_product_type)) {
      intents.usecode_product_type = editIntents.usecode_product_type
    } else {
      const saleTypes = editIntents.usecode_product_type.split(',')
      const productTypes = saleTypes
        .flatMap(st => SALE_TYPE_TO_PRODUCT_TYPES[st] ?? [])
        .join(',')
      parameters.usecode_product_type = productTypes || null
    }
  }

  // Property type: filterable value → parameters (mapped to seek codes), non-filterable → intents
  if (editIntents.usecode_property_type) {
    if (NON_FILTERABLE.has(editIntents.usecode_property_type)) {
      intents.usecode_property_type = editIntents.usecode_property_type
    } else {
      const propTypes = editIntents.usecode_property_type.split(',')
      const filterValues = propTypes
        .map(pt => PROPERTY_TYPE_TO_FILTER_VALUE[pt])
        .filter(Boolean)
        .join(',')
      parameters.usecode_property_type = filterValues || null
    }
  }

  return {
    parameters,
    intents,
  }
}

/**
 * Extract intent value for display from intents or parameters.
 * Intents take priority (they store the user's stated preference including NOT_SURE/ALL).
 */
function getDisplayIntentValue(
  intents: BuyerPreferenceIntents | null | undefined,
  params: BuyerPreferenceParameters | null | undefined,
  field: keyof BuyerPreferenceIntents
): string {
  if (intents?.[field]) return intents[field]
  // Reverse-map from parameters seek codes → intent labels
  const paramValue = params?.[field as keyof BuyerPreferenceParameters]
  if (!paramValue) return ''
  if (field === 'usecode_product_type') {
    return paramValue
      .split(',')
      .map(code => PRODUCT_TYPES_TO_SALE_TYPE[code] ?? code)
      .filter((v, i, arr) => arr.indexOf(v) === i) // dedupe (resi_bo_v2 + resi_new_foreclosed both → BANK_OWNED)
      .join(',')
  }
  if (field === 'usecode_property_type') {
    return paramValue
      .split(',')
      .map(code => FILTER_VALUE_TO_PROPERTY_TYPE[code] ?? code)
      .join(',')
  }
  return paramValue
}

interface EditState {
  parameters: BuyerPreferenceParameters
  intents: BuyerPreferenceIntents
}

const UserBuyingCriteria: React.FC<UserBuyingCriteriaProps> = ({
  preference,
  onSave,
  isSaving = false,
  collapsible = false,
  collapsed = false,
  onToggle,
  featureFlags = {},
}) => {
  const canEdit =
    featureFlags['fe.resi.enable-buybox-preferences-center-redesign'] ?? false
  const [isEditing, setIsEditing] = useState(false)
  const [editState, setEditState] = useState<EditState>({
    parameters: {},
    intents: {},
  })
  const initialEditState = useRef<EditState>({ parameters: {}, intents: {} })

  const hasChanges = useMemo(
    () =>
      JSON.stringify(editState) !== JSON.stringify(initialEditState.current),
    [editState]
  )

  const handleEdit = useCallback(() => {
    if (preference) {
      const params: BuyerPreferenceParameters = {
        ...(preference.parameters ?? {}),
      }
      const intents: BuyerPreferenceIntents = { ...(preference.intents ?? {}) }

      // Dropdown fields (MERCH-126): stored intent values take priority over
      // parameters (which are null for explicit no-constraint selections),
      // so the dropdowns reflect the saved selection.
      DROPDOWN_INTENT_KEYS.forEach((key: DropdownIntentKey) => {
        const intentValue = intents[key]
        if (intentValue && isIntentValue(intentValue)) {
          params[key] = intentValue
        }
      })

      // Reverse-map filterable seek codes from parameters back to intent
      // values so the dropdowns reflect the saved selection.
      if (!intents.occ_status && params.occ_status) {
        intents.occ_status = params.occ_status
      }
      if (!intents.usecode_product_type && params.usecode_product_type) {
        intents.usecode_product_type = params.usecode_product_type
          .split(',')
          .map(code => PRODUCT_TYPES_TO_SALE_TYPE[code] ?? code)
          .filter((v, i, arr) => arr.indexOf(v) === i)
          .join(',')
      }
      if (!intents.usecode_property_type && params.usecode_property_type) {
        intents.usecode_property_type = params.usecode_property_type
          .split(',')
          .map(code => FILTER_VALUE_TO_PROPERTY_TYPE[code] ?? code)
          .join(',')
      }

      const state: EditState = {
        parameters: { ...params },
        intents,
      }
      setEditState(state)
      initialEditState.current = state
    } else {
      const state: EditState = { parameters: {}, intents: {} }
      setEditState(state)
      initialEditState.current = state
    }
    setIsEditing(true)
  }, [preference])

  const handleSave = useCallback(() => {
    const input = buildSaveInput(editState.parameters, editState.intents)
    onSave(input)
    setIsEditing(false)
  }, [editState, onSave])

  const handleCancel = useCallback(() => {
    setIsEditing(false)
  }, [])

  // --- Parameter field handlers ---

  const handlePriceMinChange = useCallback((val: string) => {
    setEditState(prev => ({
      ...prev,
      parameters: { ...prev.parameters, price_min: parsePriceValue(val) },
    }))
  }, [])

  const handlePriceMaxChange = useCallback((val: string) => {
    setEditState(prev => ({
      ...prev,
      parameters: { ...prev.parameters, price_max: parsePriceValue(val) },
    }))
  }, [])

  const handleBedsMinChange = useCallback((val: string) => {
    setEditState(prev => ({
      ...prev,
      parameters: { ...prev.parameters, beds_min: val || null },
    }))
  }, [])

  const handleBathsMinChange = useCallback((val: string) => {
    setEditState(prev => ({
      ...prev,
      parameters: { ...prev.parameters, baths_min: val || null },
    }))
  }, [])

  const handleYrBtMinChange = useCallback((val: string) => {
    setEditState(prev => ({
      ...prev,
      parameters: { ...prev.parameters, yr_bt_min: val || null },
    }))
  }, [])

  // --- Intent field handlers ---

  const handleOccStatusChange = useCallback((val: string) => {
    setEditState(prev => ({
      ...prev,
      intents: { ...prev.intents, occ_status: val || null },
    }))
  }, [])

  const handleSaleTypeChange = useCallback(
    (val: string[]) => {
      const exclusive = ['ALL', 'NOT_SURE']
      const current = editState.intents.usecode_product_type?.split(',') ?? []
      const lastAdded = val.find(v => !current.includes(v))

      let newValue: string
      if (lastAdded && exclusive.includes(lastAdded)) {
        newValue = lastAdded
      } else {
        newValue = val.filter(v => !exclusive.includes(v)).join(',')
      }

      setEditState(prev => ({
        ...prev,
        intents: { ...prev.intents, usecode_product_type: newValue || null },
      }))
    },
    [editState.intents.usecode_product_type]
  )

  const handlePropertyTypesChange = useCallback(
    (val: string[]) => {
      const exclusive = ['NOT_SURE']
      const current = editState.intents.usecode_property_type?.split(',') ?? []
      const lastAdded = val.find(v => !current.includes(v))

      let newValue: string
      if (lastAdded && exclusive.includes(lastAdded)) {
        newValue = lastAdded
      } else {
        newValue = val.filter(v => !exclusive.includes(v)).join(',')
      }

      setEditState(prev => ({
        ...prev,
        intents: {
          ...prev.intents,
          usecode_property_type: newValue || null,
        },
      }))
    },
    [editState.intents.usecode_property_type]
  )

  // --- Location handler (stores serialized geoid string in parameters.locations) ---

  const handleLocationsChange = useCallback((locationsString: string) => {
    setEditState(prev => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        locations: locationsString || null,
      },
    }))
  }, [])

  // --- Read helpers ---

  const params = preference?.parameters
  const intents = preference?.intents

  // Dropdown read view: explicit intent value → no-constraint label,
  // concrete value → formatted, untouched → em dash
  const displayDropdownValue = (
    key: DropdownIntentKey,
    format: (v: string) => string,
    noConstraintLabel: string
  ): string => {
    const intentValue = intents?.[key]
    if (intentValue && isIntentValue(intentValue)) return noConstraintLabel
    const concrete = params?.[key]
    return concrete ? format(concrete) : '—'
  }

  const displayLocations = useMemo(() => {
    // Build a value map from parameters.locations for fallback labels
    const rawLocations = (
      preference?.parameters as Record<string, unknown> | null
    )?.locations as string | undefined
    const rawEntries = (rawLocations || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)

    const inclusions = preference?.structured_locations?.inclusions
    if (inclusions?.length) {
      return inclusions
        .filter((loc): loc is NonNullable<typeof loc> => loc != null)
        .map((loc, i) => {
          if (loc.label) return loc.label
          // Fallback: extract value portion from the raw entry at same index
          const raw = rawEntries[i]
          if (raw) {
            const idx = raw.indexOf(':')
            if (idx > 0) return raw.slice(idx + 1)
          }
          return loc.urn?.replace(/^adc:geoid:/, '') || '—'
        })
    }
    // Fallback: parse parameters.locations directly when structured_locations
    // is empty (e.g. graph resolver not deployed yet).
    if (!rawEntries.length) return []
    return rawEntries.map(entry => {
      const idx = entry.indexOf(':')
      if (idx === -1) return entry
      return entry.slice(idx + 1) || entry
    })
  }, [preference?.structured_locations?.inclusions, preference?.parameters])

  // Map geoid raw values to resolved labels for edit mode chips
  const locationLabelMap = useMemo(() => {
    const inclusions = preference?.structured_locations?.inclusions
    if (!inclusions) return {}
    const map: Record<string, string> = {}
    inclusions.forEach(loc => {
      if (!loc?.urn || !loc.label) return
      // urn is 'adc:geoid:4003114', raw stored value is 'geoid:4003114'
      const raw = loc.urn.replace(/^adc:/, '')
      map[raw] = loc.label
    })
    return map
  }, [preference?.structured_locations?.inclusions])

  if (!preference && !isEditing) {
    return (
      <CardContainer
        dropShadow='none'
        padding='none'
        borderStyle='line'
        borderColor={coreConstants.ColorNames.GrayLight}
      >
        <div
          className={cx('header', { clickable: collapsible })}
          onClick={collapsible ? onToggle : undefined}
          role={collapsible ? 'button' : undefined}
          aria-expanded={collapsible ? !collapsed : undefined}
        >
          <div className={cx('header-left')}>
            <Icon name='location-crosshairs' iconStyle='solid' color='action' />
            <Text size='large' weight='medium' color='grayDark'>
              Buying Criteria
            </Text>
          </div>
          <div className={cx('header-right')}>
            {!collapsed && canEdit && (
              <Button
                role='secondary'
                color={coreConstants.ColorNames.Action}
                size='small'
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  handleEdit()
                }}
              >
                <Icon name='plus' iconStyle='solid' /> Add
              </Button>
            )}
            {collapsible && (
              <Icon
                name={collapsed ? 'chevron-down' : 'chevron-up'}
                size='large'
                color='gray'
                iconStyle='light'
              />
            )}
          </div>
        </div>
        {!collapsed && (
          <>
            <Divider fitted />
            <div className={cx('empty-state')}>
              <Text size='small' color='grayMedium'>
                No buying criteria configured for this user.
              </Text>
            </div>
          </>
        )}
      </CardContainer>
    )
  }

  return (
    <CardContainer
      dropShadow='none'
      padding='none'
      borderStyle='line'
      borderColor={coreConstants.ColorNames.GrayLight}
    >
      {/* Header */}
      <div
        className={cx('header', { clickable: collapsible })}
        onClick={collapsible ? onToggle : undefined}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? !collapsed : undefined}
      >
        <div className={cx('header-left')}>
          <Icon
            name='location-crosshairs'
            iconStyle='solid'
            color='action'
            size='large'
          />
          <Text size='large' weight='medium' color='grayDark'>
            Buying Criteria
          </Text>
        </div>
        <div className={cx('header-right')}>
          {!collapsed && !isEditing && canEdit && (
            <Button
              role='secondary'
              color={coreConstants.ColorNames.Action}
              size='small'
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                handleEdit()
              }}
            >
              <Icon name='pen' iconStyle='solid' /> Edit
            </Button>
          )}
          {!collapsed && isEditing && (
            <div className={cx('edit-actions')}>
              <Button
                role='tertiary'
                color={coreConstants.ColorNames.Gray}
                size='small'
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  handleCancel()
                }}
              >
                Cancel
              </Button>
              <Button
                role='primary'
                color={coreConstants.ColorNames.Action}
                size='small'
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  handleSave()
                }}
                disabled={!hasChanges || isSaving}
                loading={isSaving}
              >
                Save
              </Button>
            </div>
          )}
          {collapsible && (
            <Icon
              name={collapsed ? 'chevron-down' : 'chevron-up'}
              size='large'
              color='gray'
              iconStyle='light'
            />
          )}
        </div>
      </div>

      {!collapsed && <Divider fitted />}

      {/* Content */}
      {!collapsed && (
        <div className={cx('content', 'ui', 'form')}>
          {/* Locations */}
          {isEditing ? (
            <LocationSearchInput
              value={editState.parameters.locations ?? ''}
              onChange={handleLocationsChange}
              labelMap={locationLabelMap}
              label='Locations (Where user is looking to buy)'
            />
          ) : (
            <LabelValue
              level={4}
              label='Locations (Where user is looking to buy)'
              value={
                <div className={cx('pill-group')}>
                  {displayLocations.map(loc => (
                    <Chip
                      key={loc}
                      content={loc}
                      color={ChipColor.Info}
                      size='small'
                      type='button'
                      selectionStyle='light'
                    />
                  ))}
                </div>
              }
            />
          )}

          {/* Price Range */}
          {isEditing ? (
            <div>
              <Text size='tiny' color='gray'>
                Price Range
              </Text>
              <div className={cx('min-max')}>
                <DropdownField
                  options={priceMinOptions}
                  value={priceDropdownValue(editState.parameters.price_min)}
                  onChange={handlePriceMinChange}
                  compact
                  search={false}
                />
                <span className={cx('dash')}>—</span>
                <DropdownField
                  options={priceMaxOptions}
                  value={priceDropdownValue(editState.parameters.price_max)}
                  onChange={handlePriceMaxChange}
                  compact
                  search={false}
                />
              </div>
            </div>
          ) : (
            <LabelValue
              level={4}
              classNames={{ label: cx('label') }}
              label='Price Range'
              value={
                <Text size='tiny' weight='medium' color='grayDark'>
                  {displayDropdownValue('price_min', formatPrice, 'No Min')} –{' '}
                  {displayDropdownValue('price_max', formatPrice, 'No Max')}
                </Text>
              }
            />
          )}

          {/* Occupancy / Sale Type / Property Type */}
          <div className={cx('field-grid-3')}>
            {isEditing ? (
              <DropdownField
                label='Occupancy'
                options={occupancyOptions}
                value={editState.intents.occ_status ?? ''}
                onChange={handleOccStatusChange}
                compact
                clearable
                search={false}
              />
            ) : (
              <LabelValue
                level={4}
                classNames={{ label: cx('label') }}
                label='Occupancy'
                value={
                  <Text size='tiny' weight='medium' color='grayDark'>
                    {getOptionLabel(
                      getDisplayIntentValue(intents, params, 'occ_status'),
                      occupancyOptions
                    ) || '—'}
                  </Text>
                }
              />
            )}
            {isEditing ? (
              <DropdownField
                label='Sale Type'
                options={saleTypeOptions}
                value={
                  editState.intents.usecode_product_type
                    ?.split(',')
                    .filter(Boolean) ?? []
                }
                onChange={handleSaleTypeChange}
                multiple
                compact
                useChipLabels
                search={false}
              />
            ) : (
              <LabelValue
                level={4}
                classNames={{ label: cx('label') }}
                label='Sale Type'
                value={
                  <Text size='tiny' weight='medium' color='grayDark'>
                    {getDisplayIntentValue(
                      intents,
                      params,
                      'usecode_product_type'
                    )
                      ?.split(',')
                      .map(v => getOptionLabel(v, saleTypeOptions))
                      .join(', ') || '—'}
                  </Text>
                }
              />
            )}
            {isEditing ? (
              <DropdownField
                label='Property Type'
                options={propertyTypeOptions}
                value={
                  editState.intents.usecode_property_type
                    ?.split(',')
                    .filter(Boolean) ?? []
                }
                onChange={handlePropertyTypesChange}
                multiple
                compact
                useChipLabels
                search={false}
              />
            ) : (
              <LabelValue
                level={4}
                classNames={{ label: cx('label') }}
                label='Property Type'
                value={
                  <Text size='tiny' weight='medium' color='grayDark'>
                    {getDisplayIntentValue(
                      intents,
                      params,
                      'usecode_property_type'
                    )
                      ?.split(',')
                      .map(v => getOptionLabel(v, propertyTypeOptions))
                      .join(', ') || '—'}
                  </Text>
                }
              />
            )}
          </div>

          {/* Bedrooms / Bathrooms / Year Built */}
          <div className={cx('field-grid-3')}>
            {isEditing ? (
              <DropdownField
                label='Bedrooms (Minimum)'
                options={bedroomOptions}
                value={editState.parameters.beds_min ?? ''}
                onChange={handleBedsMinChange}
                compact
                search={false}
              />
            ) : (
              <LabelValue
                level={4}
                classNames={{ label: cx('label') }}
                label='Bedrooms (Minimum)'
                value={
                  <Text size='tiny' weight='medium' color='grayDark'>
                    {displayDropdownValue('beds_min', v => v, 'Any')}
                  </Text>
                }
              />
            )}
            {isEditing ? (
              <DropdownField
                label='Bathrooms (Minimum)'
                options={bathroomOptions}
                value={editState.parameters.baths_min ?? ''}
                onChange={handleBathsMinChange}
                compact
                search={false}
              />
            ) : (
              <LabelValue
                level={4}
                classNames={{ label: cx('label') }}
                label='Bathrooms (Minimum)'
                value={
                  <Text size='tiny' weight='medium' color='grayDark'>
                    {displayDropdownValue('baths_min', v => v, 'Any')}
                  </Text>
                }
              />
            )}
            {isEditing ? (
              <DropdownField
                label='Year Built After'
                options={yearOptions}
                value={editState.parameters.yr_bt_min ?? ''}
                onChange={handleYrBtMinChange}
                compact
                search={false}
              />
            ) : (
              <LabelValue
                level={4}
                classNames={{ label: cx('label') }}
                label='Year Built After'
                value={
                  <Text size='tiny' weight='medium' color='grayDark'>
                    {displayDropdownValue('yr_bt_min', v => v, 'Any')}
                  </Text>
                }
              />
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      {!collapsed && (
        <div className={cx('footer')}>
          <Text size='mini' color='grayMedium'>
            Preferences set by user or managed by authorized internal users
          </Text>
        </div>
      )}
    </CardContainer>
  )
}

export default UserBuyingCriteria
